import { FormattedMessage } from 'react-intl';
import React from 'react';
import { AlertErrorMessage } from '../../global/modal/alerts-host/types/alert/alert-error-message';
import { JobStatus, JobType } from '../../../api/jobs.dao';
import { attendeesUploadJobProperties } from './attendees-upload';
import { generateAccessCodesJobProperties } from './generate-access-codes';
import { meetingsUploadJobProperties } from './meetings-upload/meetings-upload';
import { createZoomConnectionsForRoomsJobProperties } from './create-zoom-connections-for-rooms';
import { assignRoomsJobProperties } from './assign-rooms/assign-rooms';
import { deleteAttendeeJobProperties } from './delete-attendee/delete-attendee';
import { meetingsRemoveJobProperties } from './meetings-remove';
import { organizationsUploadJobProperties } from './organizations-upload';
import { uploadThemeArchive } from './upload-theme-archive';
import { meetingsCancelJobProperties } from './meetings-cancel';
import { generateOrganizationsLinksJobProperties } from './generate-organizations-links';
import { generatingRoomsJobProperties } from './generate-rooms';
import { operatorsUploadJobProperties } from './operators-upload';
import { accessCodesUploadJobProperties } from './access-codes-upload';
import { deleteOrganizationJobProperties } from './delete-organization';
import { attendeesUpdateBulkJobProperties } from './attendees-update-bulk';
import { meetingOperatorsUpdateBulkJobProperties } from './meetings-operators-update-bulk';
import { passportConfigurationUploadJobProperties } from './passport-configuration-upload';
import { eventBlueprintTriggerJobProperties } from './event-blueprint-trigger';
import { meetingBlueprintTriggerJobProperties } from './meeting-blueprint-trigger';
import { generateConnectionsTriggerJobProperties } from './generate-connections-trigger';
import { deleteUniqueJoiningLinksJobProperties } from './delete-unique-joining-links';


type Description = { description: string };
type Reason = { failureReason: string };

type FailedProps = { result?: Reason } & Description;

const defaultMsgProps = {
    defaultMessage: 'Unknown failure reason',
    description: 'Text',
    id: 'oe.alerts.unknownFailureReason'
};

export const JobFailedAlertTemplate = (props: FailedProps): JSX.Element => {
    const title = `${props.description} has failed.`;
    const message = props.result?.failureReason || <FormattedMessage {...defaultMsgProps} />;

    return <AlertErrorMessage title={title} message={message}/>;
};

export const JobTypeToComponentPropsMap = {
    [JobType.AttendeesUpload]: attendeesUploadJobProperties,
    [JobType.GenerateAccessCodes]: generateAccessCodesJobProperties,
    [JobType.GenerateOrganizationsLinks]: generateOrganizationsLinksJobProperties,
    [JobType.MeetingsUpload]: meetingsUploadJobProperties,
    [JobType.MeetingsCanceling]: meetingsCancelJobProperties,
    [JobType.MeetingsRemoving]: meetingsRemoveJobProperties,
    [JobType.AssignRooms]: assignRoomsJobProperties,
    [JobType.DeleteAttendee]: deleteAttendeeJobProperties,
    [JobType.GenerateRoomConnections]: createZoomConnectionsForRoomsJobProperties,
    [JobType.OperatorsUpload]: operatorsUploadJobProperties,
    [JobType.OrganizationsUpload]: organizationsUploadJobProperties,
    [JobType.AccessCodesUpload]: accessCodesUploadJobProperties,
    [JobType.UploadPassportTheme]: uploadThemeArchive,
    [JobType.GenerateRooms]: generatingRoomsJobProperties,
    [JobType.DeleteOrganization]: deleteOrganizationJobProperties,
    [JobType.AttendeeUpdateBulk]: attendeesUpdateBulkJobProperties,
    [JobType.UploadEmbeddedHTMLZip]: uploadThemeArchive,
    [JobType.MeetingOperatorsUpdateBulk]: meetingOperatorsUpdateBulkJobProperties,
    [JobType.PassportSettingsUpload]: passportConfigurationUploadJobProperties,
    [JobType.EventBlueprintTrigger]: eventBlueprintTriggerJobProperties,
    [JobType.MeetingBlueprintTrigger]: meetingBlueprintTriggerJobProperties,
    [JobType.GenerateConnectionsBlueprintTrigger]: generateConnectionsTriggerJobProperties,
    [JobType.DeleteUniqueJoiningLinks]: deleteUniqueJoiningLinksJobProperties,
};

export default new Proxy(JobTypeToComponentPropsMap, {
    get: function(target, prop) {
        if (Reflect.has(target, prop)) return {
            [JobStatus.Completed]: Reflect.get(target, prop)?.['completeComponent'],
            [JobStatus.Failed]: Reflect.get(target, prop)?.['failedComponent'] ?? JobFailedAlertTemplate
        };

        return React.Fragment;
    }
});
